import React, { createContext, useContext, useEffect, useState } from "react";
import BlockChainContext from "./blockchainContext";
import { ethers } from "ethers";
import { PrimaryInvestments } from "constants/constants";
import { AssetType, PrimaryInvestmentType } from "types";
import PrinxContext from "contexts/prinxDataContext";
import { fetchAllocations, getPricePerShare } from "./helpers/share-price-helpers";

// A note about shareprice context:
// There are two functions (and files) that get prices
// /trade/getQuote -- gets price based on input of user
// and here in sharePriceContext, we get the price of one share to display in tables

interface SharePriceContextType {
  ethersProvider: ethers.providers.Web3Provider | null;
  companyData: AssetType[];
  companyDataLoaded: boolean;
  primaryCompanyData: PrimaryInvestmentType[];
  primaryCompanyDataLoaded: boolean;
  setPrimaryCompanyDataLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  fetchSharePrices: () => Promise<void>;
}

export const SharePriceContext = createContext<SharePriceContextType>({
  ethersProvider: null,
  companyData: [],
  companyDataLoaded: false,
  primaryCompanyData: PrimaryInvestments,
  primaryCompanyDataLoaded: false,
  setPrimaryCompanyDataLoaded: () => {},
  fetchSharePrices: async () => {}
});

export const SharePriceContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { ethersProvider, smartWalletAddress } = useContext(BlockChainContext);
  const [companyData, setCompanyData] = useState<any[]>([]);
  const [companyDataLoaded, setCompanyDataLoaded] = useState(false);
  const [primaryCompanyData, setPrimaryCompanyData] = useState<any[]>(PrimaryInvestments);
  const [primaryCompanyDataLoaded, setPrimaryCompanyDataLoaded] = useState(false);
  const { assetData: assets } = useContext(PrinxContext);
  // Extract fetchSharePrices so we can expose it via context.
  const fetchSharePrices = async () => {
    if (!ethersProvider || !assets) return;
    try {
      const result = await Promise.all(
        assets.map(async (co: any, idx: number) => {
          const prices = await getPricePerShare(co);
          return {
            ...co,
            sharePrice: Number(prices.buy),
            sellSharePrice: Number(prices.sell)
          };
        })
      );
      setCompanyData(result);
      setCompanyDataLoaded(true);
    } catch (error) {
      console.error("Error fetching share prices:", error);
    }
  };

  useEffect(() => {
    if (assets && assets.length) fetchSharePrices();
    if (ethersProvider && assets && assets.length) {
      fetchAllocations(ethersProvider, setPrimaryCompanyData, setPrimaryCompanyDataLoaded, smartWalletAddress);
    }
  }, [ethersProvider, assets]);

  const value = {
    ethersProvider,
    companyData,
    companyDataLoaded,
    primaryCompanyData,
    primaryCompanyDataLoaded,
    setPrimaryCompanyDataLoaded,
    fetchSharePrices // Exposing the fetchSharePrices function
  };

  return <SharePriceContext.Provider value={value}>{children}</SharePriceContext.Provider>;
};

export default SharePriceContext;
